import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react"

import { toast } from "@coval/ui"

import { writeRecordTags } from "../utils/recordTagWrite"
import { normalizeTags } from "../utils/tagNormalization"
import {
  forgetSettledTagWrite,
  getTagWriteSnapshot,
  retainTagWrites,
  subscribeToTagWrites,
  tagWriteKey,
} from "../utils/tagWriteLedger"

interface UseRecordTagWriteParams {
  /** What the toast a failed write raises says when the error carries nothing. */
  errorFallback: string
  /** Title for the toast a failed write raises. */
  errorTitle: string
  /** Runs after a write lands, for the surfaces that read the record. */
  onWritten?: () => void
  recordId: string
  /**
   * The resource the record belongs to, e.g. `"agents"`. Namespaces the write
   * queue and ledger it shares with the bulk editor, since ids are only unique
   * within a resource.
   */
  resource: string
  /** The tags the record's row carries, which lag what its writes have set. */
  rowTags: readonly string[] | null | undefined
  /** Writes the record's whole tag list. */
  updateTags: (tags: string[]) => Promise<unknown>
}

interface RecordTagWriteResult {
  /** What the record is tagged with once its outstanding writes are counted. */
  tags: string[]
  writeTags: (tags: string[]) => void
}

/**
 * Tag editing for a single record, through the same queue and ledger the bulk
 * editor writes by.
 *
 * The tag endpoints replace a record's whole list, so a row edit and a bulk
 * edit reaching the same record are not independent: whichever the service
 * handles last wins outright, and a row that built its list before the bulk
 * write landed drops the tags that write added. Sharing the per-record queue
 * orders the two against each other, and sharing the ledger means each one
 * builds its list from what the other has already set.
 *
 * The ledger also carries what the row cannot: virtualized rows unmount on
 * scroll, and a write that moves a record out of the active filter drops its
 * row while the write is still outstanding.
 */
export function useRecordTagWrite({
  errorFallback,
  errorTitle,
  onWritten,
  recordId,
  resource,
  rowTags,
  updateTags,
}: UseRecordTagWriteParams): RecordTagWriteResult {
  const writtenTagsByKey = useSyncExternalStore(
    subscribeToTagWrites,
    getTagWriteSnapshot,
    getTagWriteSnapshot,
  )

  const normalizedRowTags = useMemo(
    () => normalizeTags(rowTags ?? []),
    [rowTags],
  )

  useEffect(() => {
    forgetSettledTagWrite(resource, recordId, normalizedRowTags)
  }, [normalizedRowTags, recordId, resource])

  // A mounted editor is a live reader of the record, so its entry outlives the
  // ledger's overflow eviction for as long as the row is on screen.
  useEffect(() => {
    return retainTagWrites(resource, [recordId])
  }, [recordId, resource])

  const tags =
    writtenTagsByKey.get(tagWriteKey(resource, recordId)) ?? normalizedRowTags

  const writeTags = useCallback(
    (nextTags: string[]) => {
      void writeRecordTags({
        recordId,
        resource,
        tags: nextTags,
        updateTags,
      }).then(({ error, landed, ownsRecord }) => {
        if (landed) {
          onWritten?.()
          return
        }

        // A newer edit that was built on this write already owns the record,
        // so its failure is that edit's to carry and not the user's to see.
        if (ownsRecord) {
          toast.error({ title: errorTitle, error, fallback: errorFallback })
        }
      })
    },
    [errorFallback, errorTitle, onWritten, recordId, resource, updateTags],
  )

  return { tags, writeTags }
}
